import {
  STAGE_UTILITY_API_BASE_URL,
  STEM_CATEGORY_ID,
  getStageUtilityHeaders,
} from "../config/stemApiConfig";

export const stemProjectApi = {
  /**
   * Calls: GET /projects/category/:categoryId
   * e.g. /projects/category/1
   */
  listActiveByCategory: async (categoryId = STEM_CATEGORY_ID) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/projects/category/${categoryId}`;
    console.log("[STEM API] GET listActiveByCategory:", { url, categoryId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });

    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });

    console.log("[STEM API] Response listActiveByCategory:", result);

    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch STEM projects");
    }

    return result.data || [];
  },

  /**
   * Calls: GET /projects/:id
   */
  getById: async (id) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/projects/${id}`;
    console.log("[STEM API] GET getById:", { url, id });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });

    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });

    console.log("[STEM API] Response getById:", result);

    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch project details");
    }

    return result.data;
  },

  /**
   * Calls: GET /projects/:projectId/stages
   * Returns active stages for the given project_id
   */
  getProjectStages: async (projectId) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/projects/${projectId}/stages`;
    console.log("[STEM API] GET getProjectStages:", { url, projectId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });

    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });

    console.log("[STEM API] Response getProjectStages:", result);

    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch project stages");
    }

    return result.data || [];
  },

  /**
   * Calls: GET /stages/:id
   */
  getStageById: async (stageId) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/stages/${stageId}`;
    console.log("[STEM API] GET getStageById:", { url, stageId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });
    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });
    console.log("[STEM API] Response getStageById:", result);
    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch stage details");
    }
    return result.data;
  },

  /**
   * Calls: GET /stages/:id/notes (for EDITOR content)
   */
  getStageNotes: async (stageId) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/stages/${stageId}/notes`;
    console.log("[STEM API] GET getStageNotes:", { url, stageId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });
    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });
    console.log("[STEM API] Response getStageNotes:", result);
    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch stage notes");
    }
    return result.data || [];
  },

  /**
   * Calls: GET /stages/:stageId/notes/:noteId/pages
   * Returns all pages for a given note: [{ id, pageNumber, body, wordCount }]
   * DevNote[AddedOn]: 03-11-2026 — Added to support PowerPoint-style page navigation
   */
  getStageNotePages: async (stageId, noteId) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/stages/${stageId}/notes/${noteId}/pages`;
    console.log("[STEM API] GET getStageNotePages:", { url, stageId, noteId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });
    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });
    console.log("[STEM API] Response getStageNotePages:", result);
    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch stage note pages");
    }
    return result.data || [];
  },

  /**
   * Calls: GET /contents?stageId=:id (for QUESTIONS content)
   */
  getStageQuestions: async (stageId) => {
    const url = `${STAGE_UTILITY_API_BASE_URL}/contents?stageId=${stageId}&limit=100`;
    console.log("[STEM API] GET getStageQuestions:", { url, stageId });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });
    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      throw new Error(`Server returned status ${res.status}`);
    });
    console.log("[STEM API] Response getStageQuestions:", result);
    if (!res.ok || !result.success) {
      throw new Error(result.message || "Failed to fetch stage questions");
    }
    return result.data || [];
  },

  /**
   * Calls: GET /stages/:stageId/fun-facts
   */
  getStageFunFacts: async (stageId, source = {}) => {
    const params = new URLSearchParams(
      source.noteId
        ? { noteId: String(source.noteId) }
        : source.fileId
        ? { fileId: String(source.fileId) }
        : {}
    );
    const queryString = params.toString();
    const url = queryString
      ? `${STAGE_UTILITY_API_BASE_URL}/stages/${stageId}/fun-facts?${queryString}`
      : `${STAGE_UTILITY_API_BASE_URL}/stages/${stageId}/fun-facts`;

    console.log("[STEM API] GET getStageFunFacts:", { url, stageId, source });
    const res = await fetch(url, {
      method: "GET",
      headers: getStageUtilityHeaders(),
    });
    const result = await res.json().catch((err) => {
      console.error("[STEM API ERROR] Failed to parse JSON response:", err);
      return { success: false, data: [] };
    });
    console.log("[STEM API] Response getStageFunFacts:", result);
    return result?.data || [];
  },
};
